import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE, GREEN] = CHART_COLORS;
const POSITIVE = ORANGE;
const NEGATIVE = BLUE;

/** Arithmetic only, rounded: SVG coordinates must match between server and browser. */
const round = (value: number): number => Math.round(value * 100) / 100;

function chargeColor(q: number): string {
  return q < 0 ? NEGATIVE : POSITIVE;
}

function Head({ id, color }: { id: string; color: string }) {
  return (
    <marker
      id={id}
      markerHeight="8"
      markerWidth="8"
      orient="auto"
      refX="7"
      refY="4"
    >
      <path d="M0,0 L8,4 L0,8 z" fill={color} />
    </marker>
  );
}

function Charge({
  x,
  y,
  q,
  name,
  radius = 17,
}: {
  x: number;
  y: number;
  q: number;
  name?: string;
  radius?: number;
}) {
  return (
    <g>
      <circle cx={x} cy={y} fill={chargeColor(q)} r={radius} />
      <text
        fill="#ffffff"
        fontSize="20"
        fontWeight="bold"
        textAnchor="middle"
        x={x}
        y={y + 7}
      >
        {q < 0 ? "−" : "+"}
      </text>
      {name ? (
        <text
          fill={CHART_INK.text}
          fontSize="14"
          fontStyle="italic"
          textAnchor="middle"
          x={x}
          y={y - radius - 6}
        >
          {name}
        </text>
      ) : null}
    </g>
  );
}

/* ── Two charges ──────────────────────────────────────────────────────────── */

/** Two charges r apart; the force arrows point away when they repel, together when they attract. */
export function PairDiagram({
  id,
  label,
  description,
  q1,
  q2,
  force,
}: {
  id: string;
  label: string;
  description: string;
  q1: number;
  q2: number;
  /** Signed force: positive repels, negative attracts, zero draws no arrows. */
  force: number;
}) {
  const x1 = 150;
  const x2 = 410;
  const y = 80;
  const arrow = `${id}-arrow`;
  const repel = force > 0;
  const length = 56;
  return (
    <ChartSvg
      description={description}
      height={160}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <Head color={CHART_INK.text} id={arrow} />
      </defs>
      <Charge name="q₁" q={q1} x={x1} y={y} />
      <Charge name="q₂" q={q2} x={x2} y={y} />
      {force === 0 ? null : (
        <g>
          <line
            markerEnd={`url(#${arrow})`}
            stroke={CHART_INK.text}
            strokeWidth="2.5"
            x1={repel ? x1 - 22 : x1 + 22}
            x2={repel ? x1 - 22 - length : x1 + 22 + length}
            y1={y}
            y2={y}
          />
          <line
            markerEnd={`url(#${arrow})`}
            stroke={CHART_INK.text}
            strokeWidth="2.5"
            x1={repel ? x2 + 22 : x2 - 22}
            x2={repel ? x2 + 22 + length : x2 - 22 - length}
            y1={y}
            y2={y}
          />
          <text
            fill={CHART_INK.text}
            fontSize="15"
            fontStyle="italic"
            textAnchor="middle"
            x={repel ? x1 - 50 : x1 + 50}
            y={y - 12}
          >
            F
          </text>
          <text
            fill={CHART_INK.text}
            fontSize="15"
            fontStyle="italic"
            textAnchor="middle"
            x={repel ? x2 + 50 : x2 - 50}
            y={y - 12}
          >
            F
          </text>
        </g>
      )}
      <line
        markerEnd={`url(#${arrow})`}
        markerStart={`url(#${arrow})`}
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={x1}
        x2={x2}
        y1={134}
        y2={134}
      />
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={(x1 + x2) / 2}
        y={126}
      >
        r
      </text>
    </ChartSvg>
  );
}

/* ── Field of one charge ──────────────────────────────────────────────────── */

const D = Math.SQRT1_2;
const RAYS = [
  [1, 0],
  [D, D],
  [0, 1],
  [-D, D],
  [-1, 0],
  [-D, -D],
  [0, -1],
  [D, -D],
] as const;

/** Radial field lines: outward from a positive charge, inward toward a negative one. */
export function RadialDiagram({
  id,
  label,
  description,
  negative,
}: {
  id: string;
  label: string;
  description: string;
  negative: boolean;
}) {
  const cx = 280;
  const cy = 100;
  const arrow = `${id}-arrow`;
  const near = 30;
  const far = 86;
  return (
    <ChartSvg
      description={description}
      height={200}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <Head color={GREEN} id={arrow} />
      </defs>
      {RAYS.map(([dx, dy]) => (
        <line
          key={`${dx}:${dy}`}
          markerEnd={`url(#${arrow})`}
          stroke={GREEN}
          strokeWidth="2.2"
          x1={round(cx + dx * (negative ? far : near))}
          x2={round(cx + dx * (negative ? near + 4 : far))}
          y1={round(cy + dy * (negative ? far : near))}
          y2={round(cy + dy * (negative ? near + 4 : far))}
        />
      ))}
      <Charge q={negative ? -1 : 1} x={cx} y={cy} />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={cx}
        x2={cx + 150}
        y1={cy}
        y2={cy}
      />
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={cx + 118}
        y={cy - 6}
      >
        r
      </text>
      <text
        fill={GREEN}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={cx + 66}
        y={cy - 34}
      >
        E
      </text>
    </ChartSvg>
  );
}

/* ── Several charges on a plane ───────────────────────────────────────────── */

export interface PlaneCharge {
  x: number;
  y: number;
  q: number;
}

interface Vec {
  x: number;
  y: number;
}

/**
 * The charges, the field point and the field contributions on a plane, to one scale.
 * Arrow lengths follow the field magnitudes (largest = 90 px) but the positions are
 * fitted to the picture, so it is a sketch rather than a plot.
 */
export function PlaneDiagram({
  id,
  label,
  description,
  charges,
  point,
  contributions,
  total,
}: {
  id: string;
  label: string;
  description: string;
  charges: readonly PlaneCharge[];
  point: Vec;
  contributions: readonly Vec[];
  total: Vec;
}) {
  const width = 560;
  const height = 300;
  const pad = 70;
  const xs = [...charges.map((c) => c.x), point.x];
  const ys = [...charges.map((c) => c.y), point.y];
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY) || 1;
  const scale = Math.min((width - 2 * pad) / span, (height - 2 * pad) / span);
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const px = (x: number) => round(width / 2 + (x - midX) * scale);
  const py = (y: number) => round(height / 2 - (y - midY) * scale);
  const magnitude = (v: Vec) => Math.sqrt(v.x * v.x + v.y * v.y);
  const largest = Math.max(
    ...contributions.map(magnitude),
    magnitude(total),
    1e-300
  );
  const gray = `${id}-gray`;
  const dark = `${id}-dark`;
  const arrowFrom = (v: Vec, color: string, w: number, marker: string) => {
    const m = magnitude(v);
    if (m === 0) {
      return null;
    }
    const len = (m / largest) * 90;
    const sx = px(point.x);
    const sy = py(point.y);
    return (
      <line
        markerEnd={`url(#${marker})`}
        stroke={color}
        strokeWidth={w}
        x1={sx}
        x2={round(sx + (v.x / m) * len)}
        y1={sy}
        y2={round(sy - (v.y / m) * len)}
      />
    );
  };
  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={width}
    >
      <defs>
        <Head color={CHART_INK.axis} id={gray} />
        <Head color={CHART_INK.text} id={dark} />
      </defs>
      {charges.map((c, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional charges
        <Charge key={i} name={`q${i + 1}`} q={c.q} x={px(c.x)} y={py(c.y)} />
      ))}
      {contributions.map((v, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional contributions
        <g key={i}>{arrowFrom(v, CHART_INK.axis, 1.8, gray)}</g>
      ))}
      {arrowFrom(total, CHART_INK.text, 3, dark)}
      <rect
        fill={GREEN}
        height="12"
        width="12"
        x={px(point.x) - 6}
        y={py(point.y) - 6}
      />
      <text
        fill={CHART_INK.text}
        fontSize="14"
        fontStyle="italic"
        textAnchor="start"
        x={px(point.x) + 10}
        y={py(point.y) + 22}
      >
        P
      </text>
    </ChartSvg>
  );
}
